import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fuel, ChevronRight, ChevronLeft, ArrowRight, Building2, Newspaper, Store, LayoutDashboard, MapPin } from 'lucide-react';
import { Button } from '../components/ui/Button';
import DemoValuationResult from '../components/valuation/DemoValuationResult';
import DemoVisualization from '../components/demo/DemoVisualization'; // Import DemoVisualization
import SEO from '../components/SEO';
import NewsletterModal from '../components/NewsletterModal';

// Sample demo data
const demoData = {
  // Basic Info
  location: '123 Main Street, Seattle, WA',
  propertyType: 'Gas Station with C-Store',
  ownership: 'owned',
  leaseTermYears: 0,
  brandAffiliation: 'branded',
  brandName: 'Shell',
  storeBrand: '7-Eleven',
  fuelSupplyYears: 5,
  
  // Operational Metrics  
  tankCapacity: 30000,
  operatingHours: '24/7',
  specificHours: '',
  numberOfPumps: 8,
  fuelTypes: ['Regular', 'Premium', 'Diesel'],
  annualGallons: 1200000,
  monthlyGallons: 100000,
  fuelMargin: 0.35,
  
  // Property Details
  propertySize: 5000,
  canopySize: 3000, // This should always match assets.Fueling_Canopy.size
  lotSize: 25000,
  yearBuilt: 2005,
  lastRenovated: 2018,
  renovationDescription: 'Updated fuel dispensers and store interior',
  propertyCondition: 'Excellent',
  zoningType: 'Commercial',
  trafficCount: 15000,
  
  // Profit Centers
  hasCarWash: true,
  carWashType: 'automatic',
  carWashRevenue: 120000,
  carWashMargin: 0.65,
  hasFoodService: true,
  foodServiceType: ['Coffee Bar', 'Hot Food'],
  foodServiceRevenue: 180000,
  foodServiceMargin: 0.55,
  foodServiceOwnership: 'owned',
  foodServiceLeaseYears: 0,
  foodServiceRent: 0,
  additionalServices: ['ATM', 'Lottery'],
  
  // Financial Metrics
  storeRevenue: 950000,
  storeMargin: 0.32, 
  totalGrossProfit: 860000,
  
  // Operating Expenses
  operatingExpenses: 390000,
  salaries: 180000,
  utilities: 48000,
  insurance: 36000,
  repairsAndMaintenance: 42000,
  propertyTax: 28000,
  advertisingAndPromotion: 18000,
  merchantFees: 24000,
  generalAndAdmin: 14000,
  isAnnual: true,
  
  // Required for ValuationResult component
  assets: {
    Fueling_Canopy: { condition: 'Excellent', size: 3000 },
    UST: { condition: 'Good', size: 30000 },
    Fueling_Equipment: { condition: 'Excellent', size: 0 },
    Site_Improvements: { condition: 'Good', size: 0 },
    Convenience_Store: { condition: 'Excellent', size: 5000 },
    Service_Station: { condition: 'Good', size: 0 },
    FFE: { condition: 'Good', size: 0 }
  }
};

// Sample valuation results breakdown
const valuationBreakdown = {
  minTotal: 2850000,
  maxTotal: 3000000,
  landValue: 750000,
  buildingSiteValue: 1800000,
  fixturesValue: 150000,
  ffeValue: 100000,
  minBusinessValue: 50000,
  maxBusinessValue: 250000
};

export default function ValuationDemo() {
  const searchParams = new URLSearchParams(window.location.search);
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(searchParams.get('results') === 'true');
  const [isPremiumFeature, setIsPremiumFeature] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);

  const steps = [
    {
      title: 'Basic Information',
      description: 'Enter key details about your gas station property',
      fields: [
        { label: 'Location', value: demoData.location },
        { label: 'Brand', value: demoData.brandName },
        { label: 'Store Type', value: demoData.storeBrand },
      ]
    },
    {
      title: 'Property Details',
      description: 'Provide information about the physical property',
      fields: [
        { label: 'Property Size', value: `${demoData.propertySize} sq ft` },
        { label: 'Year Built', value: demoData.yearBuilt },
        { label: 'Lot Size', value: `${demoData.lotSize} sq ft` },
      ]
    },
    {
      title: 'Operational Metrics',
      description: 'Share data about your gas station operations',
      fields: [
        { label: 'Annual Gallons Sold', value: `${demoData.annualGallons.toLocaleString()}` },
        { label: 'Number of Pumps', value: demoData.numberOfPumps },
        { label: 'Fuel Margin', value: `$${demoData.fuelMargin.toFixed(2)}/gallon` },
      ]
    },
    {
      title: 'Profit Centers',
      description: 'Tell us about additional revenue streams',
      fields: [
        { label: 'Car Wash', value: demoData.hasCarWash ? 'Yes' : 'No' },
        { label: 'Food Service', value: demoData.hasFoodService ? 'Yes' : 'No' },
        { label: 'Additional Services', value: demoData.additionalServices.join(', ') },
      ]
    },
    {
      title: 'Financial Metrics',
      description: 'Enter key financial data for accurate valuation',
      fields: [
        { label: 'Store Revenue', value: `$${demoData.storeRevenue.toLocaleString()}` },
        { label: 'Store Margin', value: `${(demoData.storeMargin * 100).toFixed(0)}%` },
        { label: 'Total Gross Profit', value: `$${demoData.totalGrossProfit.toLocaleString()}` },
        { label: 'Operating Expenses', value: `$${demoData.operatingExpenses.toLocaleString()}` },
      ]
    }
  ];

  const goToPreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const handleSkipToResults = () => {
    setShowResults(true);
  };

  if (showResults) {
    return (
      <>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Fuel className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold">Sample Valuation Results</h1>
                <p className="text-gray-600">
                  This is an example of what your valuation results will look like
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800">
                <strong>Demo Mode:</strong> This is a demonstration using sample data. Sign up to get accurate 
                valuations for your own properties.
              </p>
            </div>
            <DemoValuationResult 
              data={demoData} 
              valuationBreakdown={valuationBreakdown}
              onBack={() => setShowResults(false)} 
            />
          </div>
        </div>
        
        {/* Coming Soon Modules Section */}
        <div className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Coming Soon: Expanding Our Platform</h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                We're building a comprehensive suite of tools to help gas station owners, investors, and industry professionals
                make better decisions. Here's what's on our roadmap.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Marketplace */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold">Marketplace</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  A dedicated marketplace for buying and selling gas station properties. Browse listings,
                  connect with sellers, and find your next investment opportunity all in one place.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    Property listings with detailed valuations
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    Direct buyer-seller communication
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    Transaction support tools
                  </li>
                </ul>
              </div>

              {/* Industry News */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Newspaper className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold">Industry News</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Stay informed with curated industry news, market trends, regulatory updates, and insights
                  specific to the gas station and convenience store sector.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                    Daily industry news digest
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                    Regulatory and compliance updates
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                    Market trend analysis
                  </li>
                </ul>
              </div>

              {/* Vendors */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Store className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold">Vendor Directory</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Connect with trusted vendors and service providers. Find equipment suppliers, maintenance
                  services, POS systems, and more from verified industry partners.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                    Verified vendor profiles
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                    Reviews and ratings
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                    Request quotes directly
                  </li>
                </ul>
              </div>

              {/* Dashboard */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <LayoutDashboard className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold">Owner Dashboard</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  A personalized dashboard for gas station owners to track portfolio performance, manage
                  valuations, and monitor key metrics across all your properties.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                    Portfolio performance tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                    Saved valuations history
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                    Custom alerts and notifications
                  </li>
                </ul>
              </div>

              {/* Site Analysis */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold">Site Analysis</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Advanced location analytics including traffic patterns, demographic data, competitive
                  landscape, and site optimization recommendations.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                    Traffic and demographic analysis
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                    Competitor mapping
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                    Market opportunity scoring
                  </li>
                </ul>
              </div>

              {/* Industry Research */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-teal-100 rounded-lg">
                    <Fuel className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold">Industry Research</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  In-depth research reports, market studies, and data-driven insights to help you understand
                  industry trends and make informed investment decisions.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-teal-400 rounded-full"></span>
                    Quarterly market reports
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-teal-400 rounded-full"></span>
                    Regional performance benchmarks
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-teal-400 rounded-full"></span>
                    Investment opportunity analysis
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-500 mb-4">
                Want to be notified when these features launch? Create an account to stay updated.
              </p>
              <button
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center"
                onClick={() => setShowNewsletterModal(true)}
              >
                Sign Up for Updates <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Coming Soon: Dashboard & Analytics Preview */}
        <div className="bg-gray-50 py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                Coming Soon
              </div>
              <h2 className="text-3xl font-bold mb-4 flex items-center justify-center gap-2">
                <Fuel className="w-8 h-8 text-blue-600" />
                Your Personal Dashboard & Analytics
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                When you create an account, you'll get access to comprehensive analytics including performance dashboards,
                competitive intelligence, and advanced market analysis tools.
              </p>
            </div>

            {/* Full-width DemoVisualization */}
            <DemoVisualization
              showPerformanceDashboard={true}
              showCompetitorMapping={true}
            />

            <div className="text-center mt-12">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold inline-flex items-center text-lg"
                onClick={() => window.location.href = '/signup'}
              >
                Create Your Account <ArrowRight className="ml-2 w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Gas Station Valuation Demo - See How It Works"
        description="Try our interactive gas station valuation demo. See how our TAM methodology analyzes fuel volume, c-store performance, location factors, and competitive positioning to estimate property value."
        keywords="gas station valuation demo, property valuation tool, gas station valuation example, valuation calculator demo"
      />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Fuel className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold mb-2">Valuation Demo</h1>
                <p className="text-gray-600">
                  See how our valuation tool works with this interactive demo
                </p>
            </div>
          </div>
          <Button 
            onClick={handleSkipToResults} 
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center"
          >
            Skip to Results <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
          <p className="text-amber-800">
            <strong>Demo Mode:</strong> This demonstration uses sample data to show how The Fuel Central's valuation tool works. 
            You're viewing a simplified version of the full experience.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`text-sm font-medium ${
                  index <= currentStep ? 'text-blue-600' : 'text-gray-400'
                }`}
              >
                {step.title}
              </div>
            ))}
          </div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-blue-600 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-8 mb-8"
          >
            <h2 className="text-2xl font-bold mb-2">{steps[currentStep].title}</h2>
            <p className="text-gray-600 mb-6">{steps[currentStep].description}</p>
            
            {/* Form preview */}
            <div className="space-y-6 mb-6">
              {steps[currentStep].fields.map((field, index) => (
                <div key={index} className="form-preview-field">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50">
                    {field.value}
                  </div>
                </div>
              ))}
              
              {/* Show more fields teaser */}
              <div className="mt-4 p-3 border border-dashed border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-center">
                Additional fields available in the full version
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Premium Feature Overlay (random appearance) */}
        {isPremiumFeature && (
          <div className="relative -mt-4 mb-8">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/90 rounded-lg flex items-end justify-center pb-8">
              <div className="text-center">
                <span className="bg-yellow-400 text-yellow-800 text-xs font-semibold px-2.5 py-1 rounded">PREMIUM</span>
                <p className="mt-2 font-semibold">Subscribe to access advanced features</p>
                <Button className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
                  View Pricing
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={goToPreviousStep}
            disabled={currentStep === 0}
            className={`flex items-center px-4 py-2 rounded-lg ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
            }`}
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Previous
          </button>

          <button
            onClick={goToNextStep}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {currentStep === steps.length - 1 ? 'View Results' : 'Next'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    </div>
    <NewsletterModal
      isOpen={showNewsletterModal}
      onClose={() => setShowNewsletterModal(false)}
    />
    </>
  );
}
